import { useMemo, useState } from "react";
import { pluralize } from "@common/helpers/string";
import { ChartOfAccountsTemplateUploadResponse } from "@common/schemas/chart-of-accounts-template-upload.schema";
import {
    applyTopLevelType,
    buildTypeMappingRows,
    prefillTopLevelTypes,
    toTypedChart,
    TypedChartOfAccountsTemplateUpload,
    TypeMappingRow,
} from "@common/helpers/chartOfAccountsTemplateTypeMapping";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { Drawer } from "@design-system/Drawer/Drawer";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { ViewTableMeta } from "@hooks/view/useViewTableMeta";
import { ChartOfAccountsTemplateStepSubtitle } from "../ChartOfAccountsTemplateStepSubtitle";
import { IssueNavigationCommandIsland } from "../IssueNavigationCommandIsland";
import { useRowIssueNavigation } from "../useRowIssueNavigation";
import { TypeMappingCallbacks } from "./cells/TypeMappingTypeCell";
import {
    TypeMappingColumn,
    typeMappingView,
    typeMappingViewConfig,
} from "./typeMappingViewConfig";
import styles from "./ChartOfAccountsTemplateTypeMapping.module.scss";

interface Props {
    importedChart: ChartOfAccountsTemplateUploadResponse;
    isDiscardPromptOpen: boolean;
    onClose: () => void;
    onContinue: (chart: TypedChartOfAccountsTemplateUpload) => void;
}

const UNTYPED_TOOLTIP = "Every account needs a Kick type first";

export const ChartOfAccountsTemplateTypeMappingDrawer = ({
    importedChart,
    isDiscardPromptOpen,
    onClose,
    onContinue,
}: Props) => {
    const [chart, setChart] = useState(() =>
        prefillTopLevelTypes(importedChart),
    );
    const rows = useMemo(() => buildTypeMappingRows(chart), [chart]);
    const typedChart = toTypedChart(chart);
    const untypedRows = useMemo(
        () => rows.filter(({ type }) => type === null),
        [rows],
    );
    const untypedRowIds = useMemo(
        () => untypedRows.map(({ id }) => id),
        [untypedRows],
    );
    const { tableRef, currentIndex, navigateTo } = useRowIssueNavigation({
        rowIds: untypedRowIds,
        field: "type",
    });

    const tableMeta = useMemo(
        (): ViewTableMeta<TypeMappingCallbacks, never> => ({
            callbacks: {
                onTypeChange: ({ rowId, type }) =>
                    setChart((current) =>
                        applyTopLevelType({ chart: current, rowId, type }),
                    ),
            },
        }),
        [],
    );

    const handleContinue = () => {
        if (typedChart) {
            onContinue(typedChart);
        }
    };

    return (
        <Drawer
            open
            onClose={onClose}
            direction="right"
            size="fullScreen"
            noPadding
            headerAddonLeft="Map accounts into Kick types"
        >
            <div className={styles.drawerContent}>
                <ChartOfAccountsTemplateStepSubtitle>
                    Confirm where each imported account lands in Kick. You can
                    update these types later in the template editor.
                </ChartOfAccountsTemplateStepSubtitle>
                <div className={styles.tableContainer}>
                    <ViewTable<TypeMappingRow, TypeMappingColumn, never>
                        ref={tableRef}
                        data={rows}
                        view={typeMappingView}
                        config={typeMappingViewConfig}
                        tableMeta={tableMeta}
                        autoExpandOnMount
                        nestingIndentPx={24}
                        columnBorders
                        footer={
                            <IssueNavigationCommandIsland
                                completeStatus={
                                    <span className={styles.islandCount}>
                                        All {chart.accounts.length}{" "}
                                        {pluralize(
                                            "account",
                                            chart.accounts.length,
                                        )}{" "}
                                        typed
                                    </span>
                                }
                                issueLabel="Untyped"
                                issueCount={untypedRows.length}
                                currentIssueIndex={currentIndex}
                                currentIssueMessage={
                                    untypedRows[currentIndex]?.name
                                }
                                onNavigateIssue={navigateTo}
                            >
                                <ButtonWithLoader
                                    variant="primary"
                                    size="md"
                                    loading={false}
                                    disabled={
                                        !typedChart || isDiscardPromptOpen
                                    }
                                    tooltip={
                                        typedChart ? undefined : UNTYPED_TOOLTIP
                                    }
                                    hotKey="mod+enter"
                                    hotKeyOptions={{
                                        description: "Continue",
                                    }}
                                    hotKeyDisplayPosition="inline"
                                    onClick={handleContinue}
                                >
                                    Continue
                                </ButtonWithLoader>
                            </IssueNavigationCommandIsland>
                        }
                    />
                </div>
            </div>
        </Drawer>
    );
};
